import { useDocumentViewer, useDocumentViewerState } from '@st/document-viewer-kit'
import { AnnotationBase, selAnnotations } from '@st/document-viewer-kit/src/document-viewer-module'
import { StampAnnotation, User } from '@st/sdk'
import { cuid } from '@st/util/cuid'
import { Point, Size } from '@st/geom'
import { clsx } from 'clsx'
import { StampTemplate } from '../../annotations'
import { useAnnotationsContext } from '../../annotations-context'
import { recomputeStampLayoutAndStyle } from '../stamp/stamp-layout'
import { formatFullNameInitials } from '@st/util/human-name'

const STAMP_START_ORIGIN: Point = { x: 10, y: 10 }
const STAMP_SPACING: number = 0
const STAMP_SIZE: Size = { width: 80, height: 16 }

type StatusStampTemplate = {
  stampCode: string
  backgroundColor: string
  color: string
  label: string
}

const STATUS_STAMP_TEMPLATES: StatusStampTemplate[] = [
  { stampCode: 'prepared', backgroundColor: '#ef4444', color: '#ffffff', label: 'Prepared' },
  { stampCode: 'reviewed', backgroundColor: '#536cf1', color: '#ffffff', label: 'Reviewed' },
  { stampCode: 'reviewed2', backgroundColor: '#9c63ff', color: '#ffffff', label: 'Reviewed 2' }
]

const STAMP_TEMPLATES: StampTemplate[] = STATUS_STAMP_TEMPLATES.map((t, index) => {
  return {
    label: t.label,
    category: 'sign_off',
    annotation: recomputeStampLayoutAndStyle(
      {
        id: t.stampCode,
        time: '2000-01-01T00:00:00.000Z',
        pageNumber: 1,
        text: t.label,
        type: 'stamp',
        stampType: 'sign_off',
        stampSize: STAMP_SIZE.height,
        stampCode: t.stampCode,
        bounds: {
          x: STAMP_START_ORIGIN.x + (STAMP_SIZE.width + STAMP_SPACING) * index,
          y: STAMP_START_ORIGIN.y,
          width: STAMP_SIZE.width,
          height: STAMP_SIZE.height
        },
        backgroundColor: t.backgroundColor,
        color: t.color,
        font: {
          family: 'Helvetica',
          weight: 'normal',
          size: 10
        }
      },
      { width: STAMP_SIZE.width }
    )
  } satisfies StampTemplate
})

export function StatusStampsDocumentBanner({ title }: { title: string }) {
  const { currentUser } = useAnnotationsContext()
  const documentViewer = useDocumentViewer()
  const stampsInDocument = useDocumentViewerState((s) => Object.values(selAnnotations(s, isStamp)))

  return (
    <div className="pointer-events-auto flex items-center justify-between gap-x-6 bg-gray-200 px-5 text-base text-gray-900">
      <div>{title}</div>
      <div className="flex">
        {STAMP_TEMPLATES.map((template, index) => {
          const matchingStamps = stampsInDocument.filter(
            (s) => s.stampCode == template.annotation.stampCode
          )
          const isActive = matchingStamps.length > 0

          return (
            <button
              key={index}
              style={{
                backgroundColor: isActive ? template.annotation.backgroundColor : undefined,
                color: isActive ? template.annotation.color : undefined
              }}
              className={clsx('w-24 px-2 py-1', {
                'rounded-l': index == 0,
                'rounded-r': index == STATUS_STAMP_TEMPLATES.length - 1,
                'bg-white text-gray-900': !isActive
              })}
              onClick={() => {
                if (isActive) {
                  documentViewer.send({
                    type: 'deleteAnnotation',
                    annotationId: matchingStamps[0].id
                  })
                } else {
                  const annotation: StampAnnotation = {
                    ...template.annotation,
                    id: cuid(),
                    time: new Date().toISOString(),
                    authorId: currentUser?.id,
                    pageNumber: 1,
                    stampSize: STAMP_SIZE.height,
                    text: formatStampText({
                      defaultText: template.annotation.text,
                      author: currentUser,
                      now: new Date()
                    }),
                    bounds: {
                      x: STAMP_START_ORIGIN.x + (STAMP_SIZE.width + STAMP_SPACING) * index,
                      y: STAMP_START_ORIGIN.y,
                      width: STAMP_SIZE.width,
                      height: STAMP_SIZE.height
                    }
                  }
                  documentViewer.send({
                    type: 'createAnnotationDraft',
                    elementStatus: 'none',
                    annotationDraft: annotation
                  })
                  documentViewer.send({
                    type: 'createAnnotation',
                    annotationDraft: annotation
                  })
                }
              }}
            >
              {template.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function isStamp(annotation: AnnotationBase): annotation is StampAnnotation {
  return annotation.type === 'stamp'
}

function formatStampText(opts: {
  defaultText: string
  author: Partial<User> | undefined
  now: Date
}) {
  const initials = formatFullNameInitials(opts.author?.name || '')
  const name = initials || opts.defaultText
  const date = opts.now.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit'
  })
  return `${name} - ${date}`
}
